import { useContext, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";

import { DatePicker } from "components/DatePicker";
import { Dropdown } from "components/Dropdown";
import { MultilineTextField } from "components/MultilineTextField";
import { useFetchGetV2 } from "hooks/fetch/useFetchGetV2";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { UsersContext } from "contexts/UsersContext";
import { useActionAttachmentsQuery } from "hooks/queries/attachments";
import { useEventsQuery } from "hooks/queries/events";
import { useFetchPutV2 } from "hooks/fetch/useFetchPutV2";
import { AxiosContext } from "contexts/AxiosContext";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { UNASSIGNED } from "core/constants";
import { NewAttachmentsList } from "components/Attachments/NewAttachmentList";
import { ExistingAttachmentsList } from "components/Attachments/ExistingAttachmentsList";
import ProgressIndicatorOverlay from "components/ProgressIndicatorOverlay";
import { UserDropdown } from "components/UserDropdown";

const validationSchema = Yup.object({
  actionType: Yup.string()
    .label("Action")
    .required(),
  actionStatusType: Yup.string()
    .label("Status")
    .required(),
  assignee: Yup.number()
    .label("Assignee")
    .nullable(true),
  estimatedCompletionDate: Yup.date()
    .typeError("Estimated completion date must be a valid date")
    .label("Estimated completion date")
    .nullable(),
  requestedCompletionDate: Yup.date()
    .typeError("Requested completion date must be a valid date")
    .label("Requested completion date")
    .nullable(),
  description: Yup.string()
    .trim()
    .label("Description")
    .required()
});

export function EditCaseActionDialog({
  open,
  setOpen,
  caseId,
  action,
  onReplaceExistingAction
}) {
  const queryClient = useQueryClient();
  const { axios } = useContext(AxiosContext);
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const {
    loading: usersLoading,
    error: usersError,
    nonServiceAccountUsers
  } = useContext(UsersContext);

  const {
    loading: actionTypesLoading,
    error: actionTypesError,
    response: actionTypesResponse
  } = useFetchGetV2("case_action_types?ordering=name");

  const {
    loading: actionStatusTypesLoading,
    error: actionStatusTypesError,
    response: actionStatusTypesResponse
  } = useFetchGetV2("case_action_status_types");

  const {
    loading: caseAttachmentsLoading,
    error: caseAttachmentsError,
    response: caseAttachments
  } = useFetchGetV2(`cases/${caseId}/attachments`, caseId === undefined);

  const {
    data: actionAttachments,
    isError: isErrorActionAttachments,
    isLoading: isLoadingActionAttachments
  } = useActionAttachmentsQuery({
    actionId: action?.id
  });

  const { data: eventsData, isLoading: isLoadingEvents } = useEventsQuery({
    case_id__in: caseId,
    type__in: "1"
  });

  const [attachmentsToUpload, setAttachmentsToUpload] = useState([]);

  const [attachmentIdsToDelete, setAttachmentIdsToDelete] = useState([]);

  const [caseAttachmentIdsToAdd, setCaseAttachmentIdsToAdd] = useState([]);

  const [filesDropping, setFilesDropping] = useState(false);

  // TODO: add this when action attachments are returned as an object
  const selectableCaseAttachments = useMemo(() => {
    if (actionAttachments && caseAttachments) {
      const actionAttachmentIds = actionAttachments.map(
        attachment => attachment.id
      );
      return caseAttachments.filter(
        caseAttachment => !actionAttachmentIds.includes(caseAttachment.id)
      );
    }

    return caseAttachments ?? [];
  }, [caseAttachments, actionAttachments]);

  const { putCallback: putCaseAction, loading: updatingAction } = useFetchPutV2(
    `case_actions/${action?.id}`
  );

  const handleCompleted = async response => {
    addSnackbarToQueue({
      message: `Case action updated.`,
      severity: "success"
    });
    closeDialog();
    await queryClient.invalidateQueries(["streamFeed", caseId]);
    await queryClient.invalidateQueries("projects");
    await queryClient.invalidateQueries("attachments");
    onReplaceExistingAction(response);
  };

  const handleError = err => {
    console.error(err);
    addSnackbarToQueue({
      message: "Case action failed to save.",
      severity: "error"
    });
    setOpen(false);
  };

  const removeTimezoneOffset = date => {
    let formattedDate = new Date(date);
    const offset = formattedDate?.getTimezoneOffset();

    return new Date(formattedDate.getTime() - offset * 60 * 1000)
      .toISOString()
      .split("T")[0];
  };

  const makeTzSafeDateTime = date => {
    const [year, month, day] = date.split("-");
    // Most advice suggest setting the date to midnight on the date you want
    // but timezones can be -12:00 to +14:00 so we set it to 11am
    // to avoid any issues with the date being off by a day.
    // There is an edge case here where +14:00 timezone will be off by 2 hours
    // and therefore off by 1 day, but this seem like a rare issue.
    // We use 11am because we have customers in NZ where DST is +13:00
    // but no one in the -12:00 timezone.
    return new Date(year, month - 1, day, 11, 0, 0, 0);
  };

  const initialValues = useMemo(() => {
    if (action) {
      return {
        actionType: action.action_type,
        actionStatusType: action.action_status,
        assignee: action.assignee_detail?.id ?? null,
        estimatedCompletionDate: action.estimated_completion_date
          ? makeTzSafeDateTime(action.estimated_completion_date)
          : null,
        requestedCompletionDate: action.requested_completion_date
          ? makeTzSafeDateTime(action.requested_completion_date)
          : null,
        description: action.description,
        source_event: action.source_event_detail?.id ?? ""
      };
    } else {
      return {
        actionType: "",
        actionStatusType: "",
        assignee: null,
        estimatedCompletionDate: null,
        requestedCompletionDate: null,
        description: "",
        source_event: null
      };
    }
  }, [action]);

  const postDetach = async ({ actionId, attachmentId }) => {
    const { data } = await axios.post(
      `case_actions/${actionId}/attachments/detach/${attachmentId}`
    );

    return data;
  };

  const postAttach = async ({ actionId, attachmentId }) => {
    const { data } = await axios.post(
      `case_actions/${actionId}/attachments/attach/${attachmentId}`
    );

    return data;
  };

  const usePostActionAttachment = useMutation(["post-action-attachment"], {
    mutationFn: ({ actionId, file }) => {
      const formData = new FormData();
      formData.append("file", file);
      return axios
        .post(`case_actions/${actionId}/attachments`, formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        })
        .then(r => r);
    }
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async values => {
      let estimatedDate = null;
      let dueDate = null;

      // We need to convert the requestedCompletionDate to a format python accepts for a Date,
      // this will transform out date to the format "yyyy-mmm-ddd", without time and timezone adjusted.
      if (values?.estimatedCompletionDate) {
        estimatedDate = removeTimezoneOffset(values.estimatedCompletionDate);
      }
      if (values?.requestedCompletionDate) {
        dueDate = removeTimezoneOffset(values.requestedCompletionDate);
      }

      const updatedCase = {
        case: caseId,
        action_type: values.actionType,
        action_status: values.actionStatusType,
        assignee: values?.assignee ?? null,
        estimated_completion_date: estimatedDate ?? null,
        requested_completion_date: dueDate ?? null,
        description: values.description?.trim(),
        source_event: values.source_event || null
      };

      try {
        let caseAction;
        caseAction = await putCaseAction(updatedCase);

        // remove deleted attachments
        for (const attachmentId of attachmentIdsToDelete) {
          await postDetach({ actionId: caseAction.id, attachmentId });
        }

        // add case attachments
        for (const attachmentId of caseAttachmentIdsToAdd) {
          await postAttach({ actionId: caseAction.id, attachmentId });
        }

        // upload new attachments
        for (const attachment of attachmentsToUpload) {
          await usePostActionAttachment.mutateAsync({
            actionId: caseAction.id,
            file: attachment.contents
          });
        }

        await handleCompleted(caseAction);
      } catch (err) {
        await handleError(err);
      }
    }
  });

  const sourceEventValue = useMemo(
    () =>
      eventsData?.results.find(
        option => option.id === formik.values.source_event
      ) ?? null,
    [eventsData?.results, formik.values.source_event]
  );

  const setEstimatedCompletionDate = value => {
    formik.setFieldValue("estimatedCompletionDate", value);
  };

  const setRequestedCompletionDate = value => {
    formik.setFieldValue("requestedCompletionDate", value);
  };

  const onDialogClosed = (_event, reason) => {
    if (reason !== "backdropClick") closeDialog();
  };

  const closeDialog = () => {
    setAttachmentsToUpload([]);
    setAttachmentIdsToDelete([]);
    setCaseAttachmentIdsToAdd([]);
    formik.resetForm();
    setOpen(false);
  };

  const isLoading =
    actionTypesLoading ||
    actionStatusTypesLoading ||
    usersLoading ||
    updatingAction ||
    isLoadingActionAttachments ||
    caseAttachmentsLoading ||
    formik.isSubmitting;

  const lockedTooltip = action?.is_external ? "Managed by external system" : "";

  return (
    <Dialog
      open={open}
      onClose={onDialogClosed}
      aria-labelledby="edit-case-action-dialog-title"
      fullWidth
    >
      <ProgressIndicatorOverlay loading={isLoading} />
      <DialogHeaderTitle>
        <DialogHeader id="edit-case-action-dialog-title">
          Update Case Action
        </DialogHeader>
        <DialogHeaderCloseIcon onClick={closeDialog}>
          <CloseIcon />
        </DialogHeaderCloseIcon>
      </DialogHeaderTitle>
      <DialogContent dividers>
        <form
          id="edit-case-action-form"
          data-qa="CP-Form-Edit-Case-Action"
          onSubmit={formik.handleSubmit}
          noValidate
        >
          <Stack gap={2}>
            {!!action?.is_external && (
              <FormControlLabel
                control={<Checkbox name="isExternal" checked={true} disabled />}
                label="Send to work order system"
              />
            )}
            <Tooltip title={lockedTooltip}>
              <span style={{ display: "block" }}>
                <Dropdown
                  id="actionStatusType"
                  name="actionStatusType"
                  label="Status"
                  value={
                    actionStatusTypesLoading
                      ? ""
                      : formik.values.actionStatusType
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={
                    action?.is_external ||
                    actionStatusTypesLoading ||
                    !actionStatusTypesResponse?.count ||
                    actionStatusTypesError
                  }
                  data-qa="CP-Select-Form-Status"
                  error={
                    formik.touched.actionStatusType &&
                    Boolean(formik.errors.actionStatusType)
                  }
                  helperText={
                    formik.touched.actionStatusType &&
                    formik.errors.actionStatusType
                  }
                  fullWidth
                  required
                >
                  {actionStatusTypesResponse?.results?.map(({ name, id }) => (
                    <MenuItem key={id} value={id}>
                      {name}
                    </MenuItem>
                  )) ?? []}
                </Dropdown>
              </span>
            </Tooltip>
            <Tooltip title={lockedTooltip}>
              <span style={{ display: "block" }}>
                <Dropdown
                  id="actionType"
                  name="actionType"
                  label="Action"
                  value={actionTypesLoading ? "" : formik.values.actionType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={
                    action?.is_external ||
                    actionTypesLoading ||
                    !actionTypesResponse?.count ||
                    actionTypesError
                  }
                  data-qa="CP-Select-Action-Type"
                  error={
                    formik.touched.actionType &&
                    Boolean(formik.errors.actionType)
                  }
                  helperText={
                    formik.touched.actionType && formik.errors.actionType
                  }
                  fullWidth
                  required
                >
                  {actionTypesResponse?.results?.map(({ name, id }) => (
                    <MenuItem key={id} value={id}>
                      {name}
                    </MenuItem>
                  )) ?? []}
                </Dropdown>
              </span>
            </Tooltip>
            <UserDropdown
              users={nonServiceAccountUsers}
              unassignedValue={UNASSIGNED}
              error={usersError}
              helperText={usersError && "Error getting users."}
              disabled={usersLoading}
              selectedUserId={formik.values.assignee}
              onChangeSelectedUserId={newValue =>
                formik.setFieldValue("assignee", newValue)
              }
              avatarTooltip={
                "Assignee is the person the action is being assigned to"
              }
            />
            <Tooltip title={lockedTooltip}>
              <span style={{ display: "block" }}>
                <DatePicker
                  id="requestedCompletionDate"
                  date={formik.values.requestedCompletionDate}
                  onChange={setRequestedCompletionDate}
                  label="Due Date"
                  clearable
                  disabled={action?.is_external}
                  disableFuture={false}
                  fullWidth
                  size="medium"
                  slotProps={{
                    textField: {
                      id: "requestedCompletionDate",
                      onBlur: formik.handleBlur,
                      error:
                        formik.touched.requestedCompletionDate &&
                        Boolean(formik.errors.requestedCompletionDate),
                      helperText:
                        formik.touched.requestedCompletionDate &&
                        formik.errors.requestedCompletionDate
                    }
                  }}
                />
              </span>
            </Tooltip>
            <Tooltip title={lockedTooltip}>
              <span style={{ display: "block" }}>
                <DatePicker
                  id="estimatedCompletionDate"
                  date={formik.values.estimatedCompletionDate}
                  onChange={setEstimatedCompletionDate}
                  label="Estimated Completion Date"
                  clearable
                  disabled={action?.is_external}
                  disableFuture={false}
                  fullWidth
                  size="medium"
                  slotProps={{
                    textField: {
                      id: "estimatedCompletionDate",
                      onBlur: formik.handleBlur,
                      error:
                        formik.touched.estimatedCompletionDate &&
                        Boolean(formik.errors.estimatedCompletionDate),
                      helperText:
                        formik.touched.estimatedCompletionDate &&
                        formik.errors.estimatedCompletionDate
                    }
                  }}
                />
              </span>
            </Tooltip>
            <Autocomplete
              data-qa="CP-Select-Source-Event"
              loading={isLoadingEvents}
              loadingText="Loading reports..."
              options={eventsData?.results}
              value={sourceEventValue}
              getOptionLabel={option => option.option_label}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Linked Diagnostic Report"
                  name="source_event"
                  error={
                    formik.touched.source_event &&
                    Boolean(formik.errors.source_event)
                  }
                  helperText={
                    formik.touched.source_event && formik.errors.source_event
                  }
                />
              )}
              onChange={(_event, value) => {
                formik.setFieldValue("source_event", value?.id);
              }}
              onBlur={formik.handleBlur}
            />
            <MultilineTextField
              id="nca-form-problem-description"
              name="description"
              label="Description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.description && Boolean(formik.errors.description)
              }
              helperText={
                formik.touched.description && formik.errors.description
              }
              data-qa="CP-Text-Action-Description"
              required
              fullWidth
            />
            <Stack>
              <Typography component="h3" fontWeight="bold">
                New attachments
              </Typography>
              <NewAttachmentsList
                attachmentsToUpload={attachmentsToUpload}
                setAttachmentsToUpload={setAttachmentsToUpload}
                filesDropping={filesDropping}
                setFilesDropping={setFilesDropping}
              />
              {((!isLoadingActionAttachments && actionAttachments.length > 0) ||
                isLoadingActionAttachments) && (
                <Typography component="h3" fontWeight="bold">
                  Current attachments
                </Typography>
              )}
              <ProgressIndicatorContainer
                loading={isLoadingActionAttachments}
                error={isErrorActionAttachments}
              >
                <ExistingAttachmentsList
                  initialAttachments={actionAttachments}
                  setAttachmentIdsToDelete={setAttachmentIdsToDelete}
                />
              </ProgressIndicatorContainer>
              {actionAttachments && <></>}
              {((!caseAttachmentsLoading &&
                selectableCaseAttachments.length > 0) ||
                caseAttachmentsLoading) && (
                <Typography component="h3" fontWeight="bold">
                  Case attachments to include
                </Typography>
              )}
              <ProgressIndicatorContainer
                loading={caseAttachmentsLoading}
                error={caseAttachmentsError}
              >
                <ExistingAttachmentsList
                  initialAttachments={selectableCaseAttachments}
                  setSelectedAttachmentIds={setCaseAttachmentIdsToAdd}
                />
              </ProgressIndicatorContainer>
            </Stack>
          </Stack>
        </form>
      </DialogContent>
      <DialogButtonArea>
        <DialogButton
          onClick={closeDialog}
          disabled={isLoading}
          variant="outlined"
          color="primary"
          data-qa="CP-Button-Cancel-Action"
        >
          Cancel
        </DialogButton>
        <DialogButton
          type="submit"
          form="edit-case-action-form"
          disabled={isLoading || filesDropping}
          color="primary"
          variant="contained"
          data-qa="CP-Button-Submit-Action"
        >
          Submit
        </DialogButton>
      </DialogButtonArea>
    </Dialog>
  );
}

const DialogHeaderTitle = styled(DialogTitle)`
  padding: 20px 0 10px 20px;
`;

const DialogHeader = styled(Typography)`
  text-transform: none;
  font-weight: bold;
`;

const DialogHeaderCloseIcon = styled(IconButton)`
  position: absolute;
  right: 10px;
  top: 10px;
`;

const DialogButtonArea = styled(DialogActions)`
  height: 64px;
`;

const DialogButton = styled(Button)`
  padding: 6px 25px;
`;

EditCaseActionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  caseId: PropTypes.number,
  action: PropTypes.object,
  onReplaceExistingAction: PropTypes.func
};
